import { inject, Injectable } from '@angular/core';
import { ApiRouteTransactionService } from '../../../api/elite/routes/transaction.service';
import { Observable } from 'rxjs';
import {
  LotNumber,
  TransactionData,
  TransactionsRangeDto,
} from '../../../api/elite/types/transaction-data';
import { tap } from 'rxjs/operators';

import {
  Transaction,
  TransactionLine,
} from '../../../api/elite/types/transaction';

import {
  ApiNtAssemblyItemDto,
  OpenSalesOrdersOne,
} from '../../../api/netsuite/dto/assembly-item';
import { WorkOrder } from '../../../api/elite/types/work-order';
import { AssemblyItem } from '../../../api/elite/types/assembly-item';
import { ApiRouteTransactionDto } from '../../../api/elite/dto/purchase-order.dto';
import { PackingBoxSalesOrderRankApiService } from '../api/so-rank.service';
import { PackingBoxTableRow } from '../models/table-row';
import { PackingBoxSalesOrderItem } from '../models/sales-orders';

@Injectable({ providedIn: 'root' })
export class CacheTableService {
  constructor() {}
  transactionApiService = inject(ApiRouteTransactionService);
  reSoRankService = inject(PackingBoxSalesOrderRankApiService);
  savedIndices: string[] = [];
  private saleOrderCalculated = new Map<string, PackingBoxSalesOrderItem[]>();
  private salesOrderFromBackend = new Map<string, Transaction[]>();
  private salesOrderFromNestsuite = new Map<
    string,
    ApiNtAssemblyItemDto['openSalesOrders']['return']
  >();
  private numeroDeLote = new Map<string, LotNumber>();
  private workorder = new Map<string, WorkOrder>();
  private errorSalesOrder = new Map<string, boolean>();

  recalcularSalesOrder(id: string): void {
    const data = this.reSoRankService.settingUpLists(
      this.getSalesOrderFromBackend(id),
      this.getSaledOrderFromNestsuite(id)
    );
    this.saleOrderCalculated.set(id, data);
  }

  updateSalesorderQuantity(): void {}
  getDatosFromIndex(row: PackingBoxTableRow): Observable<TransactionData> {
    const transactionNumber = row.transactionNumber;
    return this.transactionApiService
      .getTransactionData(transactionNumber)
      .pipe(
        tap((data) => {
          if (data.lotNumber) {
            this.numeroDeLote.set(row.transactionNumber, data.lotNumber);
          }
          if (data.fromBack) {
            this.salesOrderFromBackend.set(
              row.transactionNumber,
              data.fromBack
            );
          }
          if (data.fromNetsuite) {
            this.salesOrderFromNestsuite.set(
              row.transactionNumber,
              data.fromNetsuite
            );
          }
          if (data.workorder) {
            this.workorder.set(row.transactionNumber, data.workorder);
          }
          if (data.hasError) {
            this.errorSalesOrder.set(row.transactionNumber, data.hasError);
          }
          this.recalcularSalesOrder(row.transactionNumber);
        })
      );
  }

  removeCacheSalesOrder(transactionNumber: string): void {
    this.salesOrderFromNestsuite.delete(transactionNumber);
    this.saleOrderCalculated.delete(transactionNumber);
    this.errorSalesOrder.delete(transactionNumber);
  }
  updateTransactionRange(
    id: string,
    data: TransactionsRangeDto
  ): Observable<Transaction[]> {
    return this.transactionApiService.updateTransactionRangeApi(data).pipe(
      tap((response) => {
        this.salesOrderFromBackend.set(id, response);
        this.recalcularSalesOrder(id);
      })
    );
  }
  getSaleOrderCalculated(id: string): PackingBoxSalesOrderItem[] {
    return this.saleOrderCalculated.get(id);
  }
  getSalesOrderFromBackend(id: string): Transaction[] {
    return this.salesOrderFromBackend.get(id);
  }
  getSaledOrderFromNestsuite(id: string) {
    return this.salesOrderFromNestsuite.get(id);
  }
  getNumeroDeLote(id: string): LotNumber {
    return this.numeroDeLote.get(id);
  }
  getWorkoreder(id: string): WorkOrder {
    return this.workorder.get(id);
  }
  getErrorSalesOrder(id: string): boolean {
    return this.errorSalesOrder.get(id);
  }
  getWorkOrderFromNetsuite(id: string): void {}
  makeSalesOrder(
    item: PackingBoxSalesOrderItem,
    netsuiteItem: OpenSalesOrdersOne,
    assemblyItem: AssemblyItem,
    transactionNumber: string
  ): void {
    const calcualteRank = this.getNextAvailableRank(
      this.getSalesOrderFromBackend(transactionNumber)
    );
    const salesOrder: ApiRouteTransactionDto['insert']['body'] = {
      transactionId: item.transactionId,
      title: item.title,
      typeOfTransaction: 'Sales Order',
      dueDate: netsuiteItem.dueDate,
      salesOrderDetails: {
        customerId: netsuiteItem.customerId,
        customerName: netsuiteItem.customerName,
        purchaseOrderTitle: item?.purchaseOrderTitle,
      },
      line: [
        {
          lineId: item.lineId,
          quantityAsked: item.quantityAsked,
          assemblyItem,
          salesOrderLineDetails: {
            partNumberByCustomer: item.partNumberByCustomer,
          },
          rank: calcualteRank,
        },
      ],
    };
    const nestsuiteData = this.getSaledOrderFromNestsuite(transactionNumber);
    const ids = nestsuiteData.map((data) => data.saleOrderId);
    this.createSalesOrder(salesOrder, ids, transactionNumber);
  }
  createSalesOrder(
    body: ApiRouteTransactionDto['insert']['body'],
    netSuiteIds: number[],
    transactionNumber: string
  ): void {
    body.netsuiteSaleordersId = netSuiteIds;
    this.transactionApiService.insertByCache(body).subscribe((data) => {
      this.salesOrderFromBackend.set(transactionNumber, data);
      this.recalcularSalesOrder(transactionNumber);
    });
  }
  getNextAvailableRank(salesOrders: Transaction[]): string {
    const allLines = salesOrders.flatMap((order) => order.lines ?? []);

    if (allLines.length === 0) {
      return '1|l';
    }

    const ranks = allLines
      .map((line) => line.rank)
      .filter((rank): rank is string => !!rank);

    if (ranks.length === 0) {
      return '1|l';
    }

    const smallestRank = ranks.sort()[0];

    return this.decrementRank(smallestRank);
  }

  decrementRank(rank: string): string {
    const [prefix, suffix] = rank.split('|');

    if (!suffix || suffix.length === 0) {
      return `${prefix}|i`;
    }

    const chars = suffix.split('');
    let i = chars.length - 1;

    while (i >= 0) {
      const code = chars[i].charCodeAt(0);

      if (code === 97) {
        chars[i] = 'z';
        i--;
      } else {
        chars[i] = String.fromCharCode(code - 1);
        return `${prefix}|${chars.join('')}`;
      }
    }

    return `${prefix}|i`;
  }
  getTransactionLineWithLowestRank(
    lines: TransactionLine[]
  ): TransactionLine | undefined {
    if (!lines || lines.length === 0) {
      return undefined;
    }

    let lowest: TransactionLine | undefined;

    for (const line of lines) {
      if (!line.rank) {
        continue;
      }

      if (!lowest) {
        lowest = line;
        continue;
      }

      if (line.rank < lowest.rank!) {
        lowest = line;
      }
    }
    return lowest ?? lines[0];
  }
}
